import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';

import { SkeletonModule } from 'primeng/skeleton';
import { MessageService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ContratoService,
  ClienteService,
} from '@core/services/domain-services';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { Cliente } from '@core/models/models';

@Component({
  selector: 'app-contrato-novo',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    InputTextModule,
    InputNumberModule,
    DatePickerModule,
    SelectModule,
    TextareaModule,

    SkeletonModule,
    BreadcrumbComponent,
  ],

  templateUrl: './contrato-novo.component.html',
  styleUrl: './contrato-novo.component.scss',
})
export class ContratoNovoComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly contratoSvc = inject(ContratoService);
  private readonly clienteSvc = inject(ClienteService);
  private readonly msg = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  form!: FormGroup;
  saving = false;

  clientes: Cliente[] = [];
  loadingClientes = false;
  clienteFixo: Cliente | null = null;
  fixedClienteId: number | null = null;

  private readonly mockClientes: Cliente[] = [
    { id: 1, nome: 'Banco BFA, S.A.', nif: '5410003691' },
    { id: 2, nome: 'Sonangol EP', nif: '5000000001' },
    { id: 3, nome: 'Angola LNG, S.A.', nif: '5000000002' },
    { id: 4, nome: 'TAAG – Linhas Aéreas', nif: '5000000003' },
  ];

  ngOnInit(): void {
    this.form = this.fb.group({
      idCliente: [null, Validators.required],
      valorMensal: [null, [Validators.required, Validators.min(1)]],
      numPostos: [1],
      inicioContrato: [null, Validators.required],
      fimContrato: [null, Validators.required],
      dataFaturacao: [null],
      observacoes: [''],
    });

    this.loadClientes();

    const cidParam = this.route.snapshot.queryParamMap.get('clienteId');
    if (cidParam) {
      const cid = Number(cidParam);
      this.fixedClienteId = cid;
      this.form.patchValue({ idCliente: cid });
      this.form.get('idCliente')?.disable();

      this.clienteSvc
        .getById(cid)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => {
            this.clienteFixo = res?.data ?? res;
          },
          error: () => {
            this.clienteFixo =
              this.mockClientes.find((c) => c.id === cid) ?? null;
          },
        });
    }

    // Update fimContrato when inicio changes (default 12 months)
    this.form
      .get('inicioContrato')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => {
        if (v && !this.form.get('fimContrato')?.value) {
          const fim = new Date(v);
          fim.setMonth(fim.getMonth() + 12);
          this.form.patchValue({ fimContrato: fim }, { emitEvent: false });
        }
      });
  }

  loadClientes(): void {
    this.loadingClientes = true;
    this.clienteSvc
      .getAll({ pageSize: 1000 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.clientes = res.data?.length ? res.data : this.mockClientes;
          this.loadingClientes = false;
        },
        error: () => {
          this.clientes = this.mockClientes;
          this.loadingClientes = false;
        },
      });
  }

  get valorAnual(): number {
    const v = this.form.get('valorMensal')?.value;
    return v ? v * 12 : 0;
  }

  get duracaoMeses(): number {
    const inicio = this.form.get('inicioContrato')?.value;
    const fim = this.form.get('fimContrato')?.value;
    if (!inicio || !fim) return 0;
    const d1 = new Date(inicio);
    const d2 = new Date(fim);
    return Math.max(
      0,
      (d2.getFullYear() - d1.getFullYear()) * 12 +
        (d2.getMonth() - d1.getMonth()),
    );
  }

  fmtKz(v: number): string {
    if (!v) return '—';
    return (
      new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(v) +
      ' Kz'
    );
  }

  save(): void {
    const raw = this.form.getRawValue();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;

    const payload = {
      idCliente: raw.idCliente,
      valorMensal: raw.valorMensal,
      numPostos: raw.numPostos ?? 1,
      inicioContrato: this.fmt(raw.inicioContrato),
      fimContrato: this.fmt(raw.fimContrato),
      dataFaturacao: this.fmt(raw.dataFaturacao),
      observacoes: raw.observacoes || '',
    };

    this.contratoSvc
      .create(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.msg.add({
            severity: 'success',
            summary: 'Contrato criado',
            detail: 'Contrato registado com sucesso.',
            life: 3000,
          });
          this.saving = false;
          const dest = this.fixedClienteId
            ? ['/admin/clientes', this.fixedClienteId]
            : ['/admin/clientes'];
          setTimeout(() => this.router.navigate(dest), 1200);
        },
        error: () => {
          this.saving = false;
        },
      });
  }

  cancelar(): void {
    if (this.fixedClienteId) {
      this.router.navigate(['/admin/clientes', this.fixedClienteId]);
    } else {
      this.router.navigate(['/admin/clientes']);
    }
  }

  private fmt(d: any): string {
    if (!d) return '';
    return d instanceof Date ? d.toISOString().split('T')[0] : String(d);
  }

  getClienteLabel(c: Cliente): string {
    return c.nif ? `${c.nome}  ·  ${c.nif}` : c.nome;
  }
}
